export type Scene7Props = {
  /**
   * Image width in pixels (int greater than 0).
   */
  width?: number | `${number}`;

  /**
   * Image height in pixels (int greater than 0).
   */
  height?: number | `${number}`;

  /**
   * JPEG quality. Specifies JPEG encoding attributes to control the compression level. This in turn varies the file size (amount of the reply data), and, indirectly, the visual quality of the resultant image.
   *
   * ref: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/r-is-http-qlt.html?lang=en
   */
  quality?: number | `${number}`;

  /**
   * Format used to download the image
   *
   * ref: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/r-is-http-fmt.html?lang=en
   */
  format?: 'jpeg' | 'png' | 'png-alpha' | 'webp' | 'webp-alpha';

  /**
   * Response Image Fit Mode. Specifies how the scale factor is calculated, which is used to scale the composite image to the response image when the response size is specified with wid= and hei=.
   *
   * - **fit**: Scales the composite image so that it fits into the space allocated with wid= and hei= , with minimal whitespace and no cropping.
   *        The response image has the exact size specified with wid= and hei=. Similar as `object-fit: contain;`
   * - **crop**: Scales the composite image so that it fills the entire response image, with minimal cropping and no whitespace. similar as `object-fit: cover;`.
   * - **stretch**: Scales the composite image independently in x and y to fill the entire response image, with no cropping and no whitespace. This typically changes the aspect ratio of the image, similar as `object-fit: fill;`
   *
   * Other options can be **constrain** and **wrap** but those will modify the real size of the image.
   *
   * ref: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/r-fit.html?lang=en
   *      https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
   */
  fit?: 'fit' | 'crop' | 'stretch' | 'constrain' | 'wrap';

  /**
   * Set upscale to true to allow upscaling or false to constrain, this value is used with **fit**
   *
   * ref: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/r-fit.html?lang=en
   */
  upscale?: boolean;

  /**
   * The position is used together with fit to specify how the img should be positioned inside its own content, values should be between -1 and 1, format: `x,y`
   *
   * - **x**: position horizontally
   *    + left: -1
   *    + center: 0
   *    + right: 1
   * - **y**: position vertically
   *    + top: -1
   *    + center: 0
   *    + bottom: 1
   *
   * ref: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/r-align.html?lang=en
   */
  position?: `${number},${number}` | { x?: number; y?: number };

  /**
   * Additional scene7 params
   *
   * Ref: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/c-command-reference.html?lang=en
   */
  additionalParams?: Record<string, string | number>;
};

// Helpers
const getFit = (
  fitMode: Scene7Props['fit'],
  upscale: Scene7Props['upscale']
) => (upscale ? `${fitMode ?? 'fit'},1` : fitMode);

const getAlign = (position: Scene7Props['position']) => {
  if (!position) return '';
  if (typeof position === 'string') {
    return position;
  }

  const { x = 0, y = 0 } = position;
  return [x, y].join();
};

/**
 * Adding props to image url to be processed from scene7 server
 * @param url Image url
 * @param props Additional Props to add to the url
 */
export const getScene7Url = (url: string | undefined, props: Scene7Props) => {
  if (!url) {
    return '';
  }
  const parsedUrl = new URL(url);
  if (url.startsWith('http:')) {
    parsedUrl.protocol = 'https';
  }

  const setParam = (param: string, value?: number | string) => {
    if (value) {
      parsedUrl.searchParams.set(param, `${value}`);
    }
  };

  setParam('wid', props.width);
  setParam('hei', props.height);
  setParam('qlt', props.quality);
  setParam('fmt', props.format);
  setParam('fit', getFit(props.fit, props.upscale));
  setParam('align', getAlign(props.position));

  if (props.additionalParams) {
    Object.entries(props.additionalParams).forEach(([param, value]) => {
      setParam(param, value);
    });
  }

  return parsedUrl.href;
};
